import type { FC, HTMLAttributes } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useModifiers } from '../../styles/hooks/useModifiers';

import { generateNumberSlideKeyframes, generateSequence } from './utils';

import styles from './NumberSlider.module.css';

export interface NumberSliderProps extends HTMLAttributes<HTMLDivElement> {
  from: number;
  to: number;
  duration: number;
}

export const NumberSlider: FC<NumberSliderProps> = ({ className, from, to, duration }) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const [isAnimate, setIsAnimate] = useState<boolean>(false);

  const items = useMemo(() => {
    const values = generateSequence(from, to, duration);
    return values.map((value, index) => (
      <li key={`${value}-${index}`} className={styles.sliderContainer__listItem}>
        {value}
      </li>
    ));
  }, [duration, from, to]);

  const mods = useModifiers(styles, 'sliderContainer', {}, { withBaseClass: true, extraClassName: className });

  useEffect(() => {
    setIsAnimate(true);
  }, [items]);

  useEffect(() => {
    if (!listRef.current) return;

    if (isAnimate) {
      const keyframes = generateNumberSlideKeyframes(items.length, from > to);
      const animation = listRef.current.animate(keyframes, { duration, easing: 'ease-out' });

      animation.onfinish = (): void => {
        setIsAnimate(false);
      };
      return (): void => animation.cancel();
    }
  }, [from, to, duration, items, isAnimate]);

  return (
    <div className={mods}>
      <ul ref={listRef} className={styles.sliderContainer__list}>
        {isAnimate ? items : <li className={styles.sliderContainer__listItem}>{to}</li>}
      </ul>
    </div>
  );
};
